import {Flexbox} from '@/components/ui/flexbox/FlexBox'

import {StyledBudgetExternalCosts, StyledBudgetExternalCostsList} from './styls'
import {useTranslation} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {Button} from '@/components/ui/button/Button'
import {AlertCircleIcon, CalendarIcon, PlusIcon} from '@/components/ui/icon/Icon'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {z} from 'zod'
import {useGetExternalCosts} from '@/features/budget/queries/useGetExternalCosts'
import {useState} from 'react'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {ExternalCostCard} from '@/features/budget/components/external-cost-card/ExternalCostCard'
import {currentYear, yearsOptions} from '@/features/budget/utils'

export const BudgetExternalCosts = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useParsedSearchParams(
        z.object({
            year: z.string().min(1).default(currentYear.toString())
        })
    )
    const externalCostsQuery = useGetExternalCosts({year: Number(searchParams.year), limit: 20})
    const [isCreateMode, setIsCreateMode] = useState(false)

    return (
        <StyledBudgetExternalCosts>
            <Flexbox align="center" justify="space-between">
                <Text size="textXl" weight="500">
                    {t('budget:external_costs_title')}
                </Text>
                <Flexbox align="center" gap={2.5}>
                    <InputSelect
                        disabled={isCreateMode}
                        optionsEntries={yearsOptions.map(year => [year, year])}
                        value={searchParams.year}
                        onChange={value => setSearchParams({year: value})}
                        displayValue={label => (
                            <>
                                <CalendarIcon />
                                {label}
                            </>
                        )}
                    />
                    <Button
                        style={{
                            flexShrink: 0
                        }}
                        variant="primaryNeutral"
                        onClick={() => setIsCreateMode(true)}
                        disabled={isCreateMode || externalCostsQuery.isPending}
                    >
                        <PlusIcon />
                        {t('budget:cost')}
                    </Button>
                </Flexbox>
            </Flexbox>
            {externalCostsQuery.isPending ? (
                <Flexbox align="center" direction="column" gap={4}>
                    <Spinner size={32} />
                </Flexbox>
            ) : externalCostsQuery.isError ? (
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            ) : !isCreateMode && externalCostsQuery.remappedData.length == 0 ? (
                <Flexbox align="center" direction="column" gap={4}>
                    <Flexbox direction="column" align="center" gap={1}>
                        <Text size="textSm" weight="500" palette="neutral" shade="600">
                            {t('budget:no_external_costs_found')}
                        </Text>
                        <Text size="textSm" weight="500" palette="neutral" shade="500">
                            {t('budget:no_external_costs_found_description')}
                        </Text>
                    </Flexbox>
                    <Button size="sm" variant="secondary" onClick={() => setIsCreateMode(true)}>
                        <PlusIcon />
                        {t('budget:no_external_costs_found_cta')}
                    </Button>
                </Flexbox>
            ) : (
                <StyledBudgetExternalCostsList
                    components={{
                        Item: props => <div className="list-item" {...props} />
                    }}
                    overscan={50}
                    data={[...(isCreateMode ? [undefined] : []), ...externalCostsQuery.remappedData]}
                    endReached={externalCostsQuery.fetchNextPage}
                    itemContent={(index, item) => (
                        <ExternalCostCard
                            key={index}
                            externalCost={item}
                            setIsCreateMode={setIsCreateMode}
                            selectedYear={Number(searchParams.year)}
                        />
                    )}
                />
            )}
        </StyledBudgetExternalCosts>
    )
}
